import { useEffect, useState } from 'react';
import { LayoutGrid, List } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Card / list switch used by the record-heavy admin pages.
 *
 * The choice is per page — someone scanning 200 applicants as rows may still
 * want reports as cards — so each caller passes its own storage key.
 */

const VIEWS = [
    { key: 'card', icon: LayoutGrid, label: 'Card view' },
    { key: 'list', icon: List,       label: 'List view' },
];

/**
 * Remembers the view across visits.
 *
 * @param storageKey  unique per page, e.g. 'echohire.resumes.view'
 * @param fallback    used the first time, before any choice is stored
 */
export function useViewMode(storageKey, fallback = 'list') {
    const [view, setView] = useState(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            return saved === 'card' || saved === 'list' ? saved : fallback;
        } catch {
            return fallback;   // private browsing / storage disabled
        }
    });

    useEffect(() => {
        try { localStorage.setItem(storageKey, view); } catch { /* non-fatal */ }
    }, [storageKey, view]);

    return [view, setView];
}

export function ViewToggle({ value, onChange, className }) {
    return (
        <div className={cn('flex items-center rounded-md border border-border p-0.5', className)}>
            {VIEWS.map(v => (
                <button
                    key={v.key}
                    type="button"
                    onClick={() => onChange(v.key)}
                    aria-label={v.label}
                    aria-pressed={value === v.key}
                    title={v.label}
                    className={cn(
                        'rounded p-1 transition-colors',
                        value === v.key
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:text-foreground',
                    )}
                >
                    <v.icon className="h-3.5 w-3.5" />
                </button>
            ))}
        </div>
    );
}

export default ViewToggle;
